import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, unref as _unref, isRef as _isRef, vModelText as _vModelText, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "mb-4"
}
const _hoisted_2 = ["value"]
const _hoisted_3 = { class: "mb-4" }
const _hoisted_4 = { class: "flex items-stretch gap-2" }
const _hoisted_5 = ["disabled"]
const _hoisted_6 = { class: "flex flex-row gap-1" }
const _hoisted_7 = ["disabled"]
const _hoisted_8 = ["disabled"]
const _hoisted_9 = { class: "mb-4" }
const _hoisted_10 = ["disabled"]
const _hoisted_11 = { class: "mb-4" }
const _hoisted_12 = { class: "flex justify-between w-full" }
const _hoisted_13 = { class: "flex items-center gap-2" }
const _hoisted_14 = ["disabled"]
const _hoisted_15 = ["disabled"]

import { ref, watch, Ref, computed } from "vue";
import { useVaccinationForm } from "@/composables/tasks/vaccination/useVaccinationForm";
import { useRecord } from "@/composables/tasks/vaccination/useVaccinationRecords";
import { useEquipment } from "@/composables/tasks/useEquipment";
import { useVaccination } from "@/composables/tasks/vaccination/useVaccination";
import { useWeight } from "@/composables/tasks/vaccination/useVaccinationWeight";
import { VaccinationProgressEntry, VaccineConfiguration } from "@/types/v2";
import { useVaccinationConfigurations } from "@/composables/tasks/vaccination/useVaccinationConfigurations";
import { useVaccinationTaskOperations } from "@/composables/tasks/vaccination/useVaccinationOperations";
import { useVaccinationProgress } from "@/composables/tasks/vaccination/useVaccinationProgress";

import ModalBox from "@/components/v2/Modal/ModalBox.vue";

// ===================================================================
// Props
// ===================================================================
interface Props {
    configuration: VaccineConfiguration;
    show: boolean;
    onClose?: () => void;
}

export default /*@__PURE__*/_defineComponent({
  __name: 'VaccinationModal',
  props: {
    configuration: {},
    show: { type: Boolean },
    onClose: { type: Function }
  },
  setup(__props: any) {

const props = __props;

// ===================================================================
// STATE - Modal
// ===================================================================
const showModal: Ref<boolean> = ref(props.show);

// ===================================================================
// COMPOSABLES
// ===================================================================
const {
    vaccinated,
    weight,
    comment,
    successMessage,

    isWeightDisabled,
    isVaccinatedDisabled,
    canSave,

    incrementVaccinated,
    decrementVaccinated,
    resetForm
} = useVaccinationForm();

const { createVaccinationRecord, createWeightRecord } = useRecord();
const { getEquipmentConfigurations } = useEquipment();
const { isIndividualWeightType, isRandomWeightType } = useWeight();

// ===================================================================
// Administration Equipment
// ===================================================================
// Get administration equipment configuration
const administrationEquipment = computed(() => {
    return getEquipmentConfigurations.value.find(config =>
        config.equipmentType.name.toLowerCase() === "administration equipment"
    );
});

// Get administration equipment options
const administrationEquipmentOptions = computed(() => {
    if (administrationEquipment.value?.equipmentType?.configOptions) {
        const typeOption = administrationEquipment.value.equipmentType.configOptions.find(opt => opt.name === "type");
        return typeOption?.options || [];
    }
    return [];
});

// Update the admin equipment type
const updateAdminEquipmentType = (type: string) => {
    if (administrationEquipment.value) {
        administrationEquipment.value.values.type = type;
        administrationEquipment.value.updated_at = new Date().toISOString();
    }
};

// ===================================================================
// ACTIONS - Modal
// ===================================================================
const closeModal = () => {
    if (props.onClose) {
        props.onClose();
    }
};

// ===================================================================
// ACTIONS - Save Vaccination Record
// ===================================================================
const handleSaveVaccinationData = (keepModalOpen = false) => {
    if (!canSave.value || !props.configuration) return;

    // Create the vaccination record (existing functionality)
    createVaccinationRecord(props.configuration.key, {
        vaccinated: vaccinated.value,
        weight: weight.value,
        comment: comment.value,
        // Include the administration equipment type
        administrationEquipment: administrationEquipment.value?.values?.type || null
    } as VaccinationProgressEntry);

    // Create a separate weight record if weight is provided (new functionality)
    if (weight.value > 0) {
        const tank = props.configuration.destinationTank;
        const group = props.configuration.destinationGroup;
        
        createWeightRecord({
            weightId: null,
            configurationKey: props.configuration.key,
            weight: weight.value,
            comment: comment.value || `Weight recorded during IVP administration`,
            timestamp: new Date().toISOString(),
            tank: tank ? {
                ...tank,
                // Ensure both name and label are available for display compatibility
                label: tank.label || tank.name,
                name: tank.name || tank.label
            } : null,
            group: group || null
        });
    }

    resetForm();

    if (!keepModalOpen) {
        return closeModal();
    }
};

// ===================================================================
// Watchers
// ===================================================================
watch(
    () => props.show,
    (newVal) => {
        showModal.value = newVal;
    }
);

return (_ctx: any,_cache: any) => {
  return (showModal.value)
    ? (_openBlock(), _createBlock(ModalBox, {
        key: 0,
        onClose: closeModal
      }, {
        header: _withCtx(() => _cache[9] || (_cache[9] = [
          _createElementVNode("h1", { class: "text-xl font-semibold" }, "Record IVP Administration", -1)
        ])),
        main: _withCtx(() => [
          (administrationEquipmentOptions.value.length > 0 && administrationEquipment.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _cache[10] || (_cache[10] = _createElementVNode("label", { class: "block text-gray-700 mb-2" }, "Administration Equipment:", -1)),
                _withDirectives(_createElementVNode("select", {
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((administrationEquipment.value.values.type) = $event)),
                  onChange: _cache[1] || (_cache[1] = ($event: any) => (updateAdminEquipmentType(administrationEquipment.value.values.type))),
                  class: "form-select bg-white p-2.5 block w-full border border-gray-300 rounded shadow-sm"
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(administrationEquipmentOptions.value, (option) => {
                    return (_openBlock(), _createElementBlock("option", {
                      key: option,
                      value: option
                    }, _toDisplayString(option), 9, _hoisted_2))
                  }), 128))
                ], 544), [
                  [_vModelSelect, administrationEquipment.value.values.type]
                ])
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_3, [
            _cache[11] || (_cache[11] = _createElementVNode("label", { class: "block text-gray-700 mb-2" }, "Administer IVP:", -1)),
            _createElementVNode("div", _hoisted_4, [
              _withDirectives(_createElementVNode("input", {
                type: "number",
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_isRef(vaccinated) ? (vaccinated).value = $event : null)),
                disabled: _unref(isVaccinatedDisabled),
                min: "1",
                pattern: "\\d*",
                class: "form-input p-2 block flex-1 border border-gray-300 rounded shadow-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:cursor-not-allowed"
              }, null, 8, _hoisted_5), [
                [
                  _vModelText,
                  _unref(vaccinated),
                  void 0,
                  { number: true }
                ]
              ]),
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("button", {
                  onClick: _cache[3] || (_cache[3] = 
//@ts-ignore
(...args) => (_unref(incrementVaccinated) && _unref(incrementVaccinated)(...args))),
                  disabled: _unref(isVaccinatedDisabled),
                  class: "p-1 w-10 text-white bg-primary rounded hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-lg font-bold"
                }, " + ", 8, _hoisted_7),
                _createElementVNode("button", {
                  onClick: _cache[4] || (_cache[4] = 
//@ts-ignore
(...args) => (_unref(decrementVaccinated) && _unref(decrementVaccinated)(...args))),
                  disabled: _unref(isVaccinatedDisabled) || _unref(vaccinated) <= 1,
                  class: "p-1 w-10 text-white bg-primary rounded hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-lg font-bold"
                }, " − ", 8, _hoisted_8)
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_9, [
            _cache[12] || (_cache[12] = _createElementVNode("label", { class: "block text-gray-700 mb-2" }, "Weight:", -1)),
            _withDirectives(_createElementVNode("input", {
              type: "number",
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => (_isRef(weight) ? (weight).value = $event : null)),
              disabled: _unref(isWeightDisabled) === true,
              min: "0",
              class: "form-input p-2 block w-full border border-gray-300 rounded shadow-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            }, null, 8, _hoisted_10), [
              [_vModelText, _unref(weight)]
            ])
          ]),
          _createElementVNode("div", _hoisted_11, [
            _cache[13] || (_cache[13] = _createElementVNode("label", { class: "block text-gray-700 mb-2" }, "Comment:", -1)),
            _withDirectives(_createElementVNode("textarea", {
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => (_isRef(comment) ? (comment).value = $event : null)),
              placeholder: "Leave a comment",
              class: "form-input p-2 block w-full border border-gray-300 rounded shadow-sm focus:ring-indigo-500 focus:border-indigo-500 min-h-[100px]"
            }, null, 512), [
              [_vModelText, _unref(comment)]
            ])
          ])
        ]),
        footer: _withCtx(() => [
          _createElementVNode("div", _hoisted_12, [
            _createElementVNode("div", _hoisted_13, [
              _createElementVNode("button", {
                onClick: _cache[7] || (_cache[7] = ($event: any) => (handleSaveVaccinationData(false))),
                type: "button",
                disabled: !_unref(canSave),
                class: "text-white bg-primary hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center disabled:opacity-50 disabled:cursor-not-allowed"
              }, " Save and close ", 8, _hoisted_14),
              _createElementVNode("button", {
                onClick: _cache[8] || (_cache[8] = ($event: any) => (handleSaveVaccinationData(true))),
                type: "button",
                disabled: !_unref(canSave),
                class: "text-white bg-green-600 hover:bg-green-500 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center disabled:opacity-50 disabled:cursor-not-allowed"
              }, _toDisplayString(_unref(successMessage) !== "" ? "Save " + _unref(successMessage) : "Save"), 9, _hoisted_15)
            ]),
            _createElementVNode("button", {
              onClick: closeModal,
              type: "button",
              class: "text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10"
            }, " Back ")
          ])
        ]),
        _: 1
      }))
    : _createCommentVNode("", true)
}
}

})